import { useState, type MouseEvent } from 'react';
import { Download, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { apiFetch } from '@/lib/api';

interface AssetDownloadButtonProps {
  workspaceId: string;
  mediaId: string | null;
  uid: string;
  className?: string;
}

// Hover-revealed download control for a generated photo or video.
//
// Assets live in a private GCS bucket behind signed URLs, so `<a download>` on the
// displayed URL does nothing (the attribute is ignored cross-origin) and fetching it
// to a blob would need bucket CORS we don't control. Instead the backend mints a fresh
// URL signed with `Content-Disposition: attachment`, which downloads on navigation.
//
// The parent tile must carry `group relative` for the hover reveal to work.
export default function AssetDownloadButton({
  workspaceId,
  mediaId,
  uid,
  className,
}: AssetDownloadButtonProps) {
  const [downloading, setDownloading] = useState(false);

  // Unsaved media has no id yet, so there is nothing to resolve the uid against.
  if (!mediaId) return null;

  const handleDownload = async (e: MouseEvent<HTMLButtonElement>) => {
    // Tiles double as selection toggles, and video tiles own their own controls.
    e.stopPropagation();
    e.preventDefault();

    setDownloading(true);
    try {
      const { url, filename } = await apiFetch<{ url: string; filename: string }>(
        `/workspaces/${workspaceId}/campaign-media/${mediaId}/assets/${uid}/download`,
      );
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to download asset');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <button
      type="button"
      title="Download"
      aria-label="Download asset"
      onClick={handleDownload}
      disabled={downloading}
      // Stays visible while the download is in flight so the spinner isn't hidden
      // if the pointer moves away mid-request.
      className={`absolute top-2 right-2 z-10 rounded-md bg-background/80 p-1.5 text-foreground shadow-sm backdrop-blur-sm transition-opacity hover:bg-background focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${downloading ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'} ${className || ''}`}
    >
      {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
    </button>
  );
}
